import {
    Button,
    Dialog,
    DialogContent,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {useSnackbar} from "notistack";
import {useLocales} from "../../../locales";
import {getLinkedTripPricePreviewAPI, getPricePreviewByLineIdRateCardId} from "../../../api/trip";
import {fCurrency} from "../../../utils/formatNumber";
import Iconify from "../../../components/Iconify";
import {getAllTicketsTypeByOperatorIdAPI} from "../../../api/ticketType/ticketType";
import PriceChangeDialog from "./PriceChangeDialog";
import {findAllRateCardsByTripId} from "../../../api/rateCard";
import {insertExceptionRateAPI} from "../../../api/rate";
import moment from "moment";
import {getAllStationsLinkedByLineId} from "../../../api/station";
import {getSessionOperator} from "../../../helper/session";
import {OPERATORS_GROUP} from "../../../constants";

export default function PriceListDialog({data, onClose}) {
    const isAdmin = getSessionOperator()?.group === OPERATORS_GROUP.superAdmin || getSessionOperator()?.group === OPERATORS_GROUP.admin
    const {enqueueSnackbar} = useSnackbar();
    const {translate} = useLocales();
    const [lastChangedField, setLastChangedField] = useState(null);
    const [loading, setLoading] = useState(false);
    const [allPricePreviewData, setAllPricePreviewData] = useState([]);
    const [discountData, setDiscountData] = useState([]);
    const [foundStations, setFoundStations] = useState([]);
    const [rateCards, setRateCards] = useState([]);
    const [selectedRateCard, setSelectedRateCard] = useState(data?.rateCardId || null);
    const [selectedStation, setSelectedStation] = useState(data?.stationId || null);
    const [selectedArrStation, setSelectedArrStation] = useState(data?.arrStationId || null);

    // Prikaz je sužen na izabrano odredište; bez izbora se vide sve stanice relacije
    const pricePreviewData = useMemo(() => (
        selectedArrStation
            ? allPricePreviewData.filter(f => f.stationId === selectedArrStation)
            : allPricePreviewData
    ), [allPricePreviewData, selectedArrStation]);
    const [openDialog, setOpenDialog] = useState({
        open: false, data: null
    });

    const [refresh, setRefresh] = useState(false);
    // Inline edit: { "stationId_ticketTypeId_key": "newValue" }
    const [pendingChanges, setPendingChanges] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        handlePrices()
    }, [refresh]);

    useEffect(() => {
        getAllStationsLinkedByLineId(data?.lineId, data?.tripId, data?.linkId).then(res => {
            setFoundStations(res?.data || []);
            if (data?.stationId) {
                setSelectedStation(res?.data.find(itm => itm.id === data.stationId).id)
            }
        })

        findAllRateCardsByTripId(data?.tripId, data?.linkId !== null).then((response) => {
            if (response.data) {
                const formatedJD = data?.journeyDate ? moment(data.journeyDate).startOf('day') : moment().startOf('day');
                // const foundRateCard = findFirstValidRateCard(response.data, formatedJD);
                // setSelectedRateCard(foundRateCard?.id);
                setRateCards(response.data);
            }
        }).catch(e => {
            console.error(e, "findAllRateCardsByTripId")
        })
    }, []);

    function findFirstValidRateCard(data, journeyDate) {

        return data.find(item => {
            const validFrom = moment(item.validFrom, 'YYYY-MM-DD').startOf('day');
            const validTo = item.validTo ? moment(item.validTo, 'YYYY-MM-DD').startOf('day') : null;

            if (validTo) {
                return journeyDate.isSameOrAfter(validFrom) && journeyDate.isBefore(validTo);
            }

            return journeyDate.isSameOrAfter(validFrom);
        });
    }

    const triggerRefresh = () => {
        setRefresh(prev => !prev);
    }
    const applyDiscounts = (price, row, key, stationItem) => {
        const {applyDiscountReturnTicket, applyDiscountOneWay, applyRoundPrice, id} = row;
        const foundException = stationItem.priceExceptionsList.find(item => item.ticketTypeId == id)
        let adjustedPrice = price;
        let discountType = "NONE";
        if (foundException) {
            const exceptionPrice = foundException[key];
            if (exceptionPrice !== undefined) {
                adjustedPrice = exceptionPrice;
                discountType = "EXCEPTION";
            } else {
                console.error("Exception price not found for key:", key, "in foundException:", foundException);
            }
        } else {
            if ((key === "priceOneWay" && applyDiscountOneWay) || (key === "returnTicketPrice" && applyDiscountReturnTicket)) {
                if (key === "priceOneWay") {
                    if (row.discountPCT > 0) {
                        adjustedPrice = price - (price * row.discountPCT / 100);
                        discountType = "PCT";
                    } else if (row.discountABS > 0) {
                        adjustedPrice = price - row.discountABS;
                        discountType = "ABS";
                    }
                } else {
                    if (row.discountPCTReturn > 0) {
                        adjustedPrice = price - (price * row.discountPCTReturn / 100);
                        discountType = "PCT";
                    } else if (row.discountABSReturn > 0) {
                        adjustedPrice = price - row.discountABSReturn;
                        discountType = "ABS";
                    }
                }
            }

            if (applyRoundPrice) {
                adjustedPrice = Math.ceil(adjustedPrice / 10) * 10;
            }

        }
        return {adjustedPrice, discountType};
    };


    const getChangeKey = (stationId, ticketTypeId, key) =>
        `${stationId}_${ticketTypeId || 'base'}_${key}`;

    const handleInlineChange = (stationId, ticketTypeId, key, value) => {
        const ck = getChangeKey(stationId, ticketTypeId, key);
        setPendingChanges(prev => ({...prev, [ck]: value}));
    };

    const handleSaveAll = async () => {
        const entries = Object.entries(pendingChanges);
        if (entries.length === 0) return;

        setSaving(true);
        let successCount = 0;
        let errorCount = 0;

        for (const [ck, newValue] of entries) {
            const [stationIdStr, ticketTypeIdStr, key] = ck.split('_');
            const stationId = Number(stationIdStr);
            const ticketTypeId = ticketTypeIdStr === 'base' ? null : Number(ticketTypeIdStr);

            // Pun skup, ne filtriran prikaz — izmena ne sme da propadne kad se promeni odredište
            const stationData = allPricePreviewData.find(p => p.stationId === stationId);
            if (!stationData) continue;

            const discount = ticketTypeId ? discountData.find(d => d.id === ticketTypeId) : null;

            const getPrice = (k) => {
                const myCk = getChangeKey(stationId, ticketTypeId, k);
                if (pendingChanges[myCk] !== undefined) return pendingChanges[myCk] || null;
                if (discount) return applyDiscounts(stationData[k], discount, k, stationData).adjustedPrice;
                return stationData[k];
            };

            const copy = {
                id: stationData.id,
                rateCardId: selectedRateCard,
                station1Id: selectedStation,
                station2Id: stationId,
                ticketTypeId: ticketTypeId,
                priceOneWay: key === 'priceOneWay' ? (newValue || null) : getPrice('priceOneWay'),
                priceReturnTicket: key === 'returnTicketPrice' ? (newValue || null) : getPrice('returnTicketPrice'),
                priceMonthly: key === 'priceMonthly' ? (newValue || null) : getPrice('priceMonthly'),
                key: key,
            };

            try {
                await insertExceptionRateAPI(copy);
                successCount++;
            } catch (e) {
                console.error(e, 'saveAll error');
                errorCount++;
            }
        }

        if (successCount > 0) enqueueSnackbar(`Sačuvano ${successCount} izmena`, {variant: "success"});
        if (errorCount > 0) enqueueSnackbar(`${errorCount} grešaka pri čuvanju`, {variant: "error"});

        setPendingChanges({});
        setSaving(false);
        triggerRefresh();
    };

    const renderTableRows = () => {
        const rows = [
            {label: "JEDAN SMER", key: "priceOneWay"},
            {label: "POVRATNA CENA", key: "returnTicketPrice"},
            {label: "DNEVNA/MESEČNA", key: "priceMonthly"},
        ];

        discountData.forEach((discount, i) => {
            if (discount.applyDiscountOneWay) {
                rows.push({
                    label: `JEDAN SMER (${discount.name})`,
                    key: "priceOneWay",
                    discount: discount
                });
            }

            if (discount.applyDiscountReturnTicket) {
                rows.push({
                    label: `POVRATNA CENA (${discount.name})`,
                    key: "returnTicketPrice",
                    discount: discount
                });
            }
        });
        return rows.map((row, rowIndex) => (
            <TableRow key={rowIndex} sx={{backgroundColor: rowIndex % 2 !== 0 ? "lightGrey" : "transparent"}}>
                <TableCell
                    sx={{
                        width: 70,
                        border: 1,
                        background: "#F4F6F8",
                        borderColor: 'divider',
                        fontSize: '0.775rem',
                        padding: '2px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                    }}>
                    {row.label}
                </TableCell>
                {pricePreviewData.map((item, index) => {
                    const ck = getChangeKey(item.stationId, row?.discount?.id, row.key);
                    const hasPending = pendingChanges[ck] !== undefined;
                    const isEditable = row.discount && data.editable && isAdmin;

                    const currentPrice = row.discount
                        ? applyDiscounts(item[row.key], row.discount, row.key, item).adjustedPrice
                        : item[row.key];

                    const isChanged = lastChangedField &&
                        lastChangedField.stationId === item.stationId &&
                        lastChangedField.ticketTypeId === row?.discount?.id &&
                        lastChangedField.key === row.key;

                    return (
                        <TableCell
                            key={index}
                            align="center"
                            sx={{
                                border: 1,
                                fontSize: '0.875rem',
                                padding: '2px 4px',
                                backgroundColor: hasPending ? 'rgba(255, 165, 0, 0.1)' : isChanged ? 'rgba(0, 128, 0, 0.1)' : 'transparent',
                                borderColor: hasPending ? 'orange' : isChanged ? 'green' : 'divider',
                            }}
                        >
                            {isEditable ? (
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        type="number"
                                        value={hasPending ? pendingChanges[ck] : (currentPrice ?? '')}
                                        onChange={(e) => handleInlineChange(item.stationId, row.discount.id, row.key, e.target.value)}
                                        InputProps={{sx: {fontSize: '0.875rem', fontWeight: 400}}}
                                        inputProps={{style: {textAlign: 'center', padding: '4px 6px'}}}
                                        sx={{
                                            width: '90px',
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                '& fieldset': {borderColor: hasPending ? 'orange' : '#bbb'},
                                                '&:hover fieldset': {borderColor: '#1976d2'},
                                                '&.Mui-focused fieldset': {borderColor: '#1976d2'},
                                            },
                                        }}
                                    />
                                ) : (
                                    <span>{currentPrice !== null ? fCurrency(`${currentPrice}`) : '/'}</span>
                                )}
                                {/*{row.discount && (*/}
                                {/*    <IconButton*/}
                                {/*        disabled={!data.editable || !isAdmin}*/}
                                {/*        onClick={() => {*/}
                                {/*            const {key} = row;*/}
                                {/*            const {stationId, id} = pricePreviewData[index];*/}
                                {/*            const priceOneWay = applyDiscounts(item['priceOneWay'], row.discount, 'priceOneWay', index).adjustedPrice;*/}
                                {/*            const priceReturnTicket = applyDiscounts(item['returnTicketPrice'], row.discount, 'returnTicketPrice', index).adjustedPrice;*/}
                                {/*            const priceMonthly = applyDiscounts(item['priceMonthly'], row.discount, 'priceMonthly', index).adjustedPrice;*/}
                                {/*            const OBJ = {*/}
                                {/*                id: id,*/}
                                {/*                rateCardId: selectedRateCard,*/}
                                {/*                station1Id: selectedStation,*/}
                                {/*                station2Id: stationId,*/}
                                {/*                ticketTypeId: row?.discount?.id || null,*/}
                                {/*                priceOneWay,*/}
                                {/*                priceReturnTicket,*/}
                                {/*                priceMonthly,*/}
                                {/*                rateCardName: item.rateCardName,*/}
                                {/*                stationName: item.stationName,*/}
                                {/*                label: row.label,*/}
                                {/*            };*/}
                                {/*            setOpenDialog({open: true, data: {...OBJ, key: key}});*/}
                                {/*        }}*/}
                                {/*        size="small"*/}
                                {/*        sx={{p: '2px'}}*/}
                                {/*    >*/}
                                {/*        <Iconify width={14} icon="mdi:eye-outline"/>*/}
                                {/*    </IconButton>*/}
                                {/*)}*/}
                        </TableCell>
                    );
                })}
            </TableRow>
        ));
    };

    const handlePrices = () => {
        if (selectedRateCard) {

            setLoading(true);
            const pricePreviewPromise = (data?.linkId !== null && data?.linkId !== undefined && data?.linkId !== "") ?
                getLinkedTripPricePreviewAPI({
                    linkID: data?.linkId,
                    rateCardId: selectedRateCard,
                    stationId: selectedStation
                }) :
                getPricePreviewByLineIdRateCardId({
                    lineId: data?.lineId,
                    depStationId: selectedStation,
                    rateCardId: selectedRateCard
                });
            const ticketTypesPromise = getAllTicketsTypeByOperatorIdAPI(data?.tripId);

            Promise.all([pricePreviewPromise, ticketTypesPromise])
                .then(([pricePreviewResponse, ticketTypesResponse]) => {
                    const prices = pricePreviewResponse?.data || [];
                    setAllPricePreviewData(prices);
                    // Posle promene polazišta izabrano odredište može da ispadne iz relacije
                    setSelectedArrStation(prev => (
                        prev && !prices.some(f => f.stationId === prev) ? null : prev
                    ));
                    setDiscountData(ticketTypesResponse?.data?.filter(f => f.operator !== null && f.active) || []);
                })
                .catch((error) => {
                    console.error(error, "Error");
                    enqueueSnackbar(translate("errorOccurred"), {variant: "error"});
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    const onRateCardChange = (e) => {
        setSelectedRateCard(e.target.value)
    }

    const onDepartureStationChange = (e) => {
        setSelectedStation(e.target.value)
    }

    const onArrivalStationChange = (e) => {
        setSelectedArrStation(e.target.value || null)
    }

    const handleSearch = () => {
        if (selectedStation && selectedRateCard) {
            setPendingChanges({});
            handlePrices()
        } else {
            enqueueSnackbar("Morate izabrati cenovnik i stanicu", {variant: 'info'})
        }
    }

    return (
        <Dialog fullWidth maxWidth="xl" open={data.open} onClose={onClose}>
            <DialogContent>
                {loading ? "Učitavanje podataka..." : (
                    <div>
                        <Stack
                            direction="row"
                            spacing={1.2}
                            justifyContent="center"
                            alignItems="center"
                            width={1}
                            mb={2}
                            sx={{position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white'}}
                        >
                            <Typography variant="subtitle1" fontSize="0.875rem">Linija: {data?.lineName}</Typography>
                            <Typography variant="subtitle1"
                                        fontSize="0.875rem">Prevoznik: {data?.operatorName}</Typography>
                        </Stack>

                        <Stack justifyContent="center" direction="row" spacing={1} mb={1}>
                            <FormControl
                                size="small"
                                sx={{width: {xs: 1, md: 440}, mb: 2}}>
                                <InputLabel sx={{'&.Mui-focused': {color: 'text.primary'}}}>
                                    Izaberite cenovnik
                                </InputLabel>
                                <Select
                                    disabled={!isAdmin}
                                    value={selectedRateCard || ""}
                                    name="selectedRateCard"
                                    onChange={onRateCardChange}
                                    input={<OutlinedInput label="Izaberite cenovnik"/>}>
                                    {rateCards.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            value={option.id}
                                            sx={{
                                                p: 1,
                                                mx: 1,
                                                my: 0.5,
                                                borderRadius: 0.75,
                                                typography: 'body2',
                                                '&:first-of-type': {mt: 0},
                                                '&:last-of-type': {mb: 0},
                                            }}>
                                            {formatRateCardOption(option)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                size="small"
                                sx={{width: {xs: 1, md: 440}, mb: 2}}>
                                <InputLabel sx={{'&.Mui-focused': {color: 'text.primary'}}}>Izaberite
                                    stanicu</InputLabel>
                                <Select
                                    value={selectedStation || ""}
                                    name="selectedStation"
                                    onChange={onDepartureStationChange}
                                    input={<OutlinedInput label="Izaberite stanicu"/>}>
                                    {foundStations.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            value={option.id}
                                            sx={{
                                                p: 1,
                                                mx: 1,
                                                my: 0.5,
                                                borderRadius: 0.75,
                                                typography: 'body2',
                                                '&:first-of-type': {mt: 0},
                                                '&:last-of-type': {mb: 0},
                                            }}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                size="small"
                                sx={{width: {xs: 1, md: 440}, mb: 2}}>
                                <InputLabel sx={{'&.Mui-focused': {color: 'text.primary'}}}>Izaberite
                                    odredište</InputLabel>
                                <Select
                                    value={selectedArrStation || ""}
                                    name="selectedArrStation"
                                    onChange={onArrivalStationChange}
                                    input={<OutlinedInput label="Izaberite odredište"/>}>
                                    <MenuItem
                                        value=""
                                        sx={{
                                            p: 1,
                                            mx: 1,
                                            my: 0.5,
                                            borderRadius: 0.75,
                                            typography: 'body2',
                                            fontStyle: 'italic',
                                        }}>
                                        Sva odredišta
                                    </MenuItem>
                                    {allPricePreviewData.map((option) => (
                                        <MenuItem
                                            key={option.stationId}
                                            value={option.stationId}
                                            sx={{
                                                p: 1,
                                                mx: 1,
                                                my: 0.5,
                                                borderRadius: 0.75,
                                                typography: 'body2',
                                                '&:last-of-type': {mb: 0},
                                            }}>
                                            {option.stationName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" sx={{width: 250}} onClick={handleSearch}>Pretraži</Button>
                        </Stack>

                        <TableContainer component={Paper} sx={{maxHeight: '70vh', overflow: 'auto'}}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            fontSize: '0.875rem',
                                            padding: '4px',
                                            position: 'sticky',
                                            top: 0,
                                            backgroundColor: 'white',
                                            zIndex: 2
                                        }}></TableCell>
                                        {pricePreviewData.map((item, index) => (
                                            <TableCell
                                                key={index}
                                                align="center"
                                                sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    fontSize: '0.875rem',
                                                    padding: '4px',
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 2
                                                }}
                                            >
                                                <Typography variant="subtitle2"
                                                            fontSize="0.875rem">{item.stationName}</Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>

                                </TableHead>
                                <TableBody>
                                    {renderTableRows()}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {Object.keys(pendingChanges).length > 0 && (
                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{mt: 2}}>
                                <Typography variant="body2" color="text.secondary">
                                    {Object.keys(pendingChanges).length} izmena za čuvanje
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setPendingChanges({})}
                                    disabled={saving}
                                >
                                    Poništi
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    size="small"
                                    loading={saving}
                                    onClick={handleSaveAll}
                                >
                                    Sačuvaj sve izmene
                                </LoadingButton>
                            </Stack>
                        )}
                    </div>
                )}
            </DialogContent>
            <PriceChangeDialog
                setLoading={setLoading}
                loading={loading}
                open={openDialog.open}
                data={openDialog.data}
                handleClose={(changedField) => {
                    setOpenDialog({open: false, data: null});
                    if (changedField) {
                        setLastChangedField(changedField);
                    }
                    triggerRefresh();
                }}
            />
        </Dialog>
    );
}

const formatRateCardOption = (option) => {
    if (!option) return '';

    const name = option.name || '';
    const validFrom = option.validFrom
        ? moment(option.validFrom).format('DD.MM.YYYY')
        : '/';

    const validTo = option.validTo
        ? moment(option.validTo).format('DD.MM.YYYY')
        : '/';

    return `${name} (${validFrom} - ${validTo})`;
};
